import React, { Suspense, useMemo } from 'react';
import Stack from '@mui/material/Stack/Stack';
import { PageContainer } from '../components/PageContainer';
import AppHeader from '../components/AppHeader/AppHeader';
import { Link, Outlet, useParams } from 'react-router-dom';
import { useCrew } from '../hooks/useCrews';
import { useCurrentCrewMember } from '../hooks/useCurrentCrewMember';
import { usePublicCrewInfo } from '../hooks/usePublicCrewInfo';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';
import { AxiosError } from 'axios';
import { GlassBox, H4 } from '../components/ui';
import Container from '@mui/material/Container';
import DefaultAppHeader from '../components/AppHeader/DefaultAppHeader';
import { createTheme } from '@mui/material/styles';
import ThemeProvider from '@mui/material/styles/ThemeProvider';
import baseTheme, { baseThemeOptions } from '../theme';
import { useAtomValue } from 'jotai';
import { crewThemeOverrideAtom } from '../store/atoms';
import { SuspendedCrewRecovery } from '../components/crew/SuspendedCrewRecovery';
import { useSse } from '../hooks/useSse';
import { CrewPreview } from '../components/crew/CrewPreview';

export const CrewWrapper = () => {
    const { slug } = useParams<{ slug: string }>();
    const { data: currentCrew, isLoading: loading, error } = useCrew(slug!);
    const { data: publicCrewInfo, isLoading: publicCrewLoading } = usePublicCrewInfo(slug);

    const { data: crewMember, isLoading: crewMemberLoading } = useCurrentCrewMember(slug);

    // Persistent SSE connection for real-time notifications and insight status updates
    useSse(slug ?? '');

    // Live preview override written by AppearanceSettings while the user is editing
    const themeOverride = useAtomValue(crewThemeOverrideAtom);

    const crewTheme = useMemo(() => {
        // Use the live editing override if it belongs to this crew, otherwise fall back to saved config
        const cfg = (themeOverride?.slug === slug ? themeOverride?.themeConfig : null)
            ?? currentCrew?.themeConfig;
        if (!cfg?.primaryColor && !cfg?.secondaryColor && !cfg?.backgroundColor && !cfg?.darkMode
            && !cfg?.errorColor && !cfg?.warningColor && !cfg?.infoColor && !cfg?.successColor) return baseTheme;
        return createTheme({
            ...baseThemeOptions,
            palette: {
                ...(cfg.darkMode && { mode: 'dark' as const }),
                ...(cfg.primaryColor && { primary: { main: cfg.primaryColor } }),
                ...(cfg.secondaryColor && { secondary: { main: cfg.secondaryColor } }),
                ...(cfg.backgroundColor && { background: { default: cfg.backgroundColor } }),
                ...(cfg.errorColor && { error: { main: cfg.errorColor } }),
                ...(cfg.warningColor && { warning: { main: cfg.warningColor } }),
                ...(cfg.infoColor && { info: { main: cfg.infoColor } }),
                ...(cfg.successColor && { success: { main: cfg.successColor } })
            }
        });
    }, [themeOverride, currentCrew?.themeConfig, slug]);

    if (error) {
        const errorMessage = (error as AxiosError<{ error: string }>).response?.data?.error || 'Failed to load crew';
        
        // If we got a 403 (permission error) and have public crew info, show the crew preview.
        // Don't pass the error string — non-membership is expected and the preview handles join flow.
        if ((error as AxiosError).response?.status === 403 && publicCrewInfo && !publicCrewLoading) {
            return (
                <>
                    <DefaultAppHeader />
                    <CrewPreview crew={publicCrewInfo} />
                </>
            );
        }

        return (
            <>
                <DefaultAppHeader />

                <Container sx={{ mt: 4 }}>
                    <GlassBox py={8}>
                        <Stack spacing={2} alignItems="center">
                            <H4>Error Loading Crew</H4>
                            <Typography>{errorMessage}</Typography>
                            <Typography>Have an invite code? <Link to={'/join'}>Join here</Link></Typography>
                        </Stack>
                    </GlassBox>
                </Container>
            </>
        );
    }

    if (loading || crewMemberLoading || publicCrewLoading) {
        return (
            <Container sx={{ mt: 4 }}>
                <GlassBox py={8} alignItems={'center'}>
                    <Stack spacing={2} alignItems="center">
                        <CircularProgress size={60} />
                    </Stack>
                </GlassBox>
            </Container>
        );
    }

    if (!currentCrew || !crewMember) {
        return <></>;
    }


    return (
        <ThemeProvider theme={crewTheme}>
            <AppHeader crew={currentCrew} profile={crewMember} />
            {(currentCrew.status === 'suspended' || currentCrew.status === 'archived') && (
                <SuspendedCrewRecovery crew={currentCrew} />
            )}
            {currentCrew.status !== 'archived' && (
                <div
                    className={crewTheme.palette.mode === 'dark' ? 'dark-theme' : 'light-theme'}
                >
                    <Suspense>
                        <Outlet />
                    </Suspense>
                </div>
            )}
        </ThemeProvider>
    );
};

